import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { SkipLink } from '@/components/SkipLink';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { userApi, ordersApi, addressesApi, UserProfile, Order } from '@/lib/api';
import { toast } from 'sonner';
import {
  User,
  Package,
  MapPin,
  Lock,
  Mail,
  Phone,
  Calendar,
  Euro,
  Loader2,
  Edit,
  Save,
  X,
  AlertCircle,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

const profileSchema = z.object({
  first_name: z.string().min(2, 'Nome deve contenere almeno 2 caratteri').optional(),
  last_name: z.string().min(2, 'Cognome deve contenere almeno 2 caratteri').optional(),
  display_name: z.string().min(2, 'Nome visualizzato deve contenere almeno 2 caratteri').optional(),
});

const passwordSchema = z.object({
  current_password: z.string().min(6, 'Password corrente richiesta'),
  new_password: z.string().min(6, 'Nuova password deve contenere almeno 6 caratteri'),
  confirm_password: z.string().min(6, 'Conferma password richiesta'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: 'Le password non corrispondono',
  path: ['confirm_password'],
});

type ProfileFormValues = z.infer<typeof profileSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

const Account = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Usa React Query per cache automatica
  const { data: profile, isLoading: isLoadingProfile, refetch: refetchProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => userApi.getProfile(),
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minuti
    gcTime: 30 * 60 * 1000, // 30 minuti
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: ordersData, isLoading: isLoadingOrders } = useQuery({
    queryKey: ['user-orders', 1],
    queryFn: () => ordersApi.getUserOrders(1, 10),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minuti (ordini più dinamici)
    gcTime: 15 * 60 * 1000, // 15 minuti
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const orders = ordersData?.orders || [];
  const isLoading = isLoadingProfile || isLoadingOrders;

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
      toast.error('Devi essere autenticato per accedere al tuo account');
      return;
    }
  }, [isAuthenticated, authLoading]);

  // Pre-compila il form quando il profilo viene caricato
  useEffect(() => {
    if (profile) {
      profileForm.reset({
        first_name: profile.first_name,
        last_name: profile.last_name,
        display_name: profile.display_name,
      });
    }
  }, [profile]);

  const onProfileSubmit = async (data: ProfileFormValues) => {
    try {
      await userApi.updateProfile(data);
      setIsEditingProfile(false);
      // Invalida e ricarica la cache
      refetchProfile();
      toast.success('Profilo aggiornato con successo!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Errore nell\'aggiornamento del profilo');
    }
  };

  const onPasswordSubmit = async (data: PasswordFormValues) => {
    try {
      await userApi.changePassword(data.current_password, data.new_password);
      setIsChangingPassword(false);
      passwordForm.reset();
      toast.success('Password cambiata con successo!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Errore nel cambio password');
    }
  };

  const getOrderStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      pending: { label: 'In attesa', variant: 'outline' },
      processing: { label: 'In elaborazione', variant: 'default' },
      'on-hold': { label: 'In sospeso', variant: 'secondary' },
      completed: { label: 'Completato', variant: 'default' },
      cancelled: { label: 'Annullato', variant: 'destructive' },
      refunded: { label: 'Rimborsato', variant: 'secondary' },
      failed: { label: 'Fallito', variant: 'destructive' },
    };

    const statusInfo = statusMap[status] || { label: status, variant: 'outline' as const };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (authLoading || isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
        <Footer />
      </>
    );
  }

  if (!isAuthenticated || !profile) {
    return null;
  }

  return (
    <>
      <SEO
        title="Il Mio Account - Brace e Sapori Store"
        description="Gestisci il tuo account, visualizza i tuoi ordini, modifica le tue informazioni personali e gestisci i tuoi indirizzi di spedizione."
        noindex={true}
      />
      <SkipLink />
      <Header />
      <main className="min-h-screen py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Il Mio Account</h1>
            <p className="text-muted-foreground">Benvenuto, {profile.display_name || profile.email}</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile">
                <User className="mr-2 h-4 w-4" />
                Profilo
              </TabsTrigger>
              <TabsTrigger value="orders">
                <Package className="mr-2 h-4 w-4" />
                Ordini
              </TabsTrigger>
              <TabsTrigger value="addresses">
                <MapPin className="mr-2 h-4 w-4" />
                Indirizzi
              </TabsTrigger>
              <TabsTrigger value="password">
                <Lock className="mr-2 h-4 w-4" />
                Password
              </TabsTrigger>
            </TabsList>

            {/* TAB PROFILO */}
            <TabsContent value="profile" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Informazioni Personali</CardTitle>
                      <CardDescription>Gestisci le tue informazioni personali</CardDescription>
                    </div>
                    {!isEditingProfile && (
                      <Button variant="outline" onClick={() => setIsEditingProfile(true)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Modifica
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isEditingProfile ? (
                    <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="first_name">Nome</Label>
                          <Input
                            id="first_name"
                            {...profileForm.register('first_name')}
                            defaultValue={profile.first_name}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="last_name">Cognome</Label>
                          <Input
                            id="last_name"
                            {...profileForm.register('last_name')}
                            defaultValue={profile.last_name}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="display_name">Nome Visualizzato</Label>
                        <Input
                          id="display_name"
                          {...profileForm.register('display_name')}
                          defaultValue={profile.display_name}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" value={profile.email} disabled />
                        <p className="text-sm text-muted-foreground">L'email non può essere modificata</p>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit">
                          <Save className="mr-2 h-4 w-4" />
                          Salva
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsEditingProfile(false);
                            profileForm.reset();
                          }}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Annulla
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-muted-foreground">Nome</Label>
                          <p className="font-medium">{profile.first_name || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Cognome</Label>
                          <p className="font-medium">{profile.last_name || '-'}</p>
                        </div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Email</Label>
                        <p className="font-medium flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {profile.email}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Nome Visualizzato</Label>
                        <p className="font-medium">{profile.display_name}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB ORDINI */}
            <TabsContent value="orders" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>I Miei Ordini</CardTitle>
                  <CardDescription>Visualizza lo storico dei tuoi ordini</CardDescription>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>Non hai ancora effettuato ordini</AlertDescription>
                    </Alert>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <Card key={order.id}>
                          <CardContent className="pt-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold">Ordine #{order.id}</span>
                                  {getOrderStatusBadge(order.status)}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatDate(order.date_created)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Euro className="h-4 w-4" />
                                    {parseFloat(order.total).toFixed(2)} {order.currency}
                                  </span>
                                </div>
                                <div className="text-sm">
                                  <p className="font-medium">{order.line_items.length} prodotto/i</p>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                onClick={() => navigate(`/ordine/${order.id}`)}
                              >
                                Dettagli
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB INDIRIZZI */}
            <TabsContent value="addresses" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Indirizzi</CardTitle>
                  <CardDescription>Gestisci i tuoi indirizzi di fatturazione e spedizione</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-4">Indirizzo di Fatturazione</h3>
                    <div className="bg-muted/50 p-4 rounded-lg space-y-2 border">
                      {profile.billing.address_1 ? (
                        <>
                          <p className="font-medium">
                            {profile.billing.first_name} {profile.billing.last_name}
                          </p>
                          <p className="text-muted-foreground">{profile.billing.address_1}</p>
                          {profile.billing.address_2 && <p className="text-muted-foreground">{profile.billing.address_2}</p>}
                          <p className="text-muted-foreground">
                            {profile.billing.postcode} {profile.billing.city} ({profile.billing.state})
                          </p>
                          {profile.billing.phone && (
                            <p className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-4 w-4" />
                              {profile.billing.phone}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-muted-foreground">Nessun indirizzo di fatturazione configurato</p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold mb-4">Indirizzo di Spedizione</h3>
                    <div className="bg-muted/50 p-4 rounded-lg space-y-2 border">
                      {profile.shipping.address_1 ? (
                        <>
                          <p className="font-medium">
                            {profile.shipping.first_name} {profile.shipping.last_name}
                          </p>
                          <p className="text-muted-foreground">{profile.shipping.address_1}</p>
                          {profile.shipping.address_2 && <p className="text-muted-foreground">{profile.shipping.address_2}</p>}
                          <p className="text-muted-foreground">
                            {profile.shipping.postcode} {profile.shipping.city} ({profile.shipping.state})
                          </p>
                        </>
                      ) : (
                        <p className="text-muted-foreground">Nessun indirizzo di spedizione configurato</p>
                      )}
                    </div>
                  </div>

                  <Button variant="outline" onClick={() => navigate('/checkout')}>
                    <MapPin className="mr-2 h-4 w-4" />
                    Modifica Indirizzi al Prossimo Checkout
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB PASSWORD */}
            <TabsContent value="password" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Cambia Password</CardTitle>
                      <CardDescription>Aggiorna la tua password per mantenere il tuo account sicuro</CardDescription>
                    </div>
                    {!isChangingPassword && (
                      <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Modifica
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isChangingPassword ? (
                    <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="current_password">Password Corrente</Label>
                        <Input
                          id="current_password"
                          type="password"
                          {...passwordForm.register('current_password')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new_password">Nuova Password</Label>
                        <Input
                          id="new_password"
                          type="password"
                          {...passwordForm.register('new_password')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm_password">Conferma Nuova Password</Label>
                        <Input
                          id="confirm_password"
                          type="password"
                          {...passwordForm.register('confirm_password')}
                        />
                        {passwordForm.formState.errors.confirm_password && (
                          <p className="text-sm text-red-500">
                            {passwordForm.formState.errors.confirm_password.message}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit">
                          <Save className="mr-2 h-4 w-4" />
                          Salva
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsChangingPassword(false);
                            passwordForm.reset();
                          }}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Annulla
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <Alert>
                      <Lock className="h-4 w-4" />
                      <AlertDescription>
                        La tua password è stata impostata al momento della registrazione. Clicca su "Modifica" per cambiarla.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Account;

